// Friendly-error mapping per ARCHITECTURE.md.
// Never surface a raw `TypeError` or "Failed to fetch" to the user.

export type ErrorCode =
  | 'bad_request'
  | 'not_found'
  | 'payload_too_large'
  | 'unsupported_media'
  | 'ai_busy'
  | 'ai_unavailable'
  | 'ai_bad_request'
  | 'storage_unavailable'
  | 'internal'
  | 'network'
  | 'unknown';

export interface ErrorEnvelope {
  error: string;
  code: ErrorCode | string;
}

/**
 * `ApiError` instances from `api/client.ts` carry an HTTP `status`.
 * Optional field — old envelopes from `fetch()` rejection (no body) won't
 * have it, so the field is optional.
 */
export interface MaybeStatusError {
  code?: string;
  error?: string;
  status?: number;
}

const FRIENDLY: Record<ErrorCode, string> = {
  bad_request: 'Some settings look off — please check the form.',
  not_found: "Couldn't find that one.",
  payload_too_large: 'That file is too big — max 10 MB.',
  unsupported_media: "That file type isn't supported — try PNG, JPG, or WebP.",
  ai_busy: 'The AI is busy — please try again in a moment.',
  ai_unavailable: "Can't reach the AI right now. Try again in a minute.",
  ai_bad_request: "The AI didn't like that combination of settings.",
  storage_unavailable: 'Storage is unavailable right now. Try again in a moment.',
  internal: 'Something went wrong on our end. Try again.',
  network: "Can't reach the server right now. Check your connection and try again.",
  unknown: 'Something unexpected happened. Try again.',
};

const KNOWN_CODES: ReadonlySet<string> = new Set<string>([
  'bad_request',
  'not_found',
  'payload_too_large',
  'unsupported_media',
  'ai_busy',
  'ai_unavailable',
  'ai_bad_request',
  'storage_unavailable',
  'internal',
]);

/**
 * Derive the user-facing ErrorCode from an error.
 *
 * Resolution order:
 *   1. AbortError (user-cancelled) → 'unknown' with a "Cancelled." message.
 *   2. Network failure (fetch TypeError, status === 0, etc.) → 'network'.
 *   3. Envelope has a known, SPECIFIC code (anything except the generic
 *      catch-alls `internal` and `unknown`) → use that code. Status is
 *      ignored in this branch.
 *   4. HTTP status present:
 *        - 5xx → 'ai_unavailable' — the most likely user-facing cause
 *          of a 5xx (the AI can't be reached). Specifically catches the
 *          pre-generatedExists FK-crash where the backend returned 500
 *          with `code: "internal"`, which used to surface as
 *          "Something went wrong on our end" instead of the actionable
 *          "Can't reach the AI right now. Try again in a minute."
 *        - 4xx → 'bad_request' — generic 4xx with no useful body.
 *   5. Anything else → 'unknown'.
 */
export function friendlyError(err: unknown): { code: ErrorCode; message: string } {
  const obj = err && typeof err === 'object' ? (err as MaybeStatusError) : null;
  const maybeStatus = obj?.status;

  // AbortError from fetch — caller cancelled, don't shout at the user.
  if (err instanceof DOMException && err.name === 'AbortError') {
    return { code: 'unknown', message: 'Cancelled.' };
  }

  // Status-aware fallback — runs FIRST so a 5xx with the generic
  // `internal` code (the most common backend-emitted shape on a crash)
  // is remapped to `ai_unavailable` instead of leaking the unhelpful
  // "Something went wrong on our end" copy.
  if (maybeStatus !== undefined) {
    // A specific body code (`storage_unavailable`, `not_found`, etc.)
    // wins over the status fallback — those messages carry real
    // information mum can act on.
    if (obj?.code && KNOWN_CODES.has(obj.code) && obj.code !== 'internal') {
      const code = obj.code as ErrorCode;
      return { code, message: FRIENDLY[code] };
    }
    if (maybeStatus >= 500 && maybeStatus < 600) {
      return { code: 'ai_unavailable', message: FRIENDLY.ai_unavailable };
    }
    if (maybeStatus >= 400 && maybeStatus < 500) {
      return { code: 'bad_request', message: FRIENDLY.bad_request };
    }
  }

  // Envelope without a status (rare — only old callers that hand-rolled
  // a thrown object). Honour the body code as-is.
  if (err && typeof err === 'object' && 'code' in err && 'error' in err) {
    const envelope = err as ErrorEnvelope;
    const code = KNOWN_CODES.has(envelope.code)
      ? (envelope.code as ErrorCode)
      : 'unknown';
    return { code, message: FRIENDLY[code] };
  }

  // Network failures (fetch throws TypeError on offline / CORS / DNS).
  if (err instanceof TypeError) {
    return { code: 'network', message: FRIENDLY.network };
  }

  return { code: 'unknown', message: FRIENDLY.unknown };
}

/** Best-effort code extraction; used to decide retry behaviour, etc. */
export function errorCode(err: unknown): ErrorCode {
  const obj = err && typeof err === 'object' ? (err as MaybeStatusError) : null;
  const maybeStatus = obj?.status;

  if (obj?.code && KNOWN_CODES.has(obj.code) && obj.code !== 'internal') {
    return obj.code as ErrorCode;
  }
  if (maybeStatus !== undefined) {
    if (maybeStatus >= 500 && maybeStatus < 600) return 'ai_unavailable';
    if (maybeStatus >= 400 && maybeStatus < 500) return 'bad_request';
  }
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: unknown }).code);
    if (KNOWN_CODES.has(code)) return code as ErrorCode;
  }
  if (err instanceof TypeError) return 'network';
  return 'unknown';
}
